import { useState, useEffect, useCallback } from 'react'
import { LocalStorageHelper } from '../helpers/localStorageHelper'
import { use } from 'chai'

export function useLocalStorage<T>(key: string, initialValue: T) {
  // Get initial value from localStorage or use initialValue
  const [storedValue, setStoredValue] = useState<T>(() => {
    return initialValue
  })

  // Set initial value in localStorage if it doesn't exist
  useEffect(() => {
    const item = LocalStorageHelper.get<T>(key)
    if (item !== null) {
      setStoredValue(item)
    } else {
      LocalStorageHelper.set(key, initialValue)
    }
  }, [])

  // Return a wrapped version of localStorage's setter that updates the state
  const setValue = useCallback(
    (value: T | ((val: T) => T)) => {
      // Allow value to be a function so we have same API as useState
      const valueToStore = value instanceof Function ? value(storedValue) : value

      // Save state
      setStoredValue(valueToStore)
      // Save to localStorage
      LocalStorageHelper.set(key, valueToStore)
    },
    [key, storedValue],
  )

  // Listen to storage changes in other windows/tabs
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === key && e.newValue !== null) {
        setStoredValue(JSON.parse(e.newValue))
      }
    }

    window.addEventListener('storage', handleStorageChange)
    return () => window.removeEventListener('storage', handleStorageChange)
  }, [key])

  return [storedValue, setValue] as const
}
